import { currentSession } from '@/api/client'
import { ouvrirRuntimeIdentite, runtimeIdentite } from './runtime'

/**
 * L'ONGLET ADOPTE LE COMPTE AVEC LEQUEL IL TRAVAILLE.
 *
 * ─── Le défaut que ce module ferme ──────────────────────────────────────────
 *
 * Le backoffice n'appelait `useInThisTab` QUE lors d'un changement de compte
 * depuis le widget. À l'entrée par laissez-passer — le chemin normal — l'onglet
 * n'avait donc aucun slot actif : `runtime.currentSession()` rendait `null`.
 *
 * Or c'est sur ce slot que repose la surveillance du trousseau : sans lui, elle
 * sort à sa première ligne et ne regarde jamais rien. Mesuré en production par
 * le banc croisé — déconnexion depuis Sign, le kiosque sortait, le backoffice
 * restait ouvert avec un widget annonçant « Connectée » une identité qui
 * n'existait plus.
 *
 * C'est le même manque qu'ADR-016 §14.13, qui avait été corrigé chez Sign et au
 * kiosque et oublié ici : les trois applications ont la même colle, écrite
 * trois fois (§14.14).
 *
 * ─── Comment le slot est choisi ─────────────────────────────────────────────
 *
 * Par l'ADRESSE de la session en place, quand on la connaît : c'est la seule
 * désignation exacte quand plusieurs comptes vivent dans le trousseau. À
 * défaut, le compte que la porte vient d'élire par défaut, ou l'unique vivant.
 * On LIT ce choix, on ne l'impose pas — le trousseau appartient à son gardien.
 *
 * DÉGRADÉ, JAMAIS BLOQUANT : trousseau injoignable, la session en place est
 * servie quand même. Retenir quelqu'un dehors parce qu'un menu manque serait
 * absurde ; la surveillance reprendra au prochain chargement.
 */
export async function adopterLeCompteDeCetOnglet(): Promise<void> {
  try {
    // Sans session locale, il n'y a rien à rattacher — et interroger le BFF
    // pour un visiteur non connecté dépenserait son quota pour rien.
    const session = currentSession()
    if (session === null) return

    const runtime = runtimeIdentite() ?? ouvrirRuntimeIdentite()
    // Déjà adopté (rechargement du même onglet) : le choix de l'onglet lui
    // appartient, le réécrire pourrait défaire une bascule volontaire.
    if (runtime.currentSession() !== null) return

    const vivants = (await runtime.listBrowserSessions()).filter((s) => s.state === 'live')
    const adresse = session.user?.email ?? ''
    const cible =
      (adresse !== '' ? vivants.find((s) => s.email === adresse) : undefined) ??
      vivants.find((s) => s.isDefault) ??
      (vivants.length === 1 ? vivants[0] : undefined)

    if (cible === undefined) {
      // Une session locale sans compte vivant correspondant dans le trousseau
      // n'est pas un état normal — et c'est le symptôme qu'un trousseau
      // invisible produit. Le dire, plutôt que de laisser la surveillance
      // aveugle sans que rien ne l'explique.
      // sast:derogation(SAST-005) navigateur : aucun canal assaini n'existe cote
      // SPA, et le message ne porte AUCUNE valeur externe — un compte, un etat
      // de trousseau et rien d'autre, donc pas de CWE-117.
      console.warn(
        `[backoffice] adoption : aucun compte vivant ne correspond (${vivants.length} vivant(s))`,
      )
      return
    }
    await runtime.useInThisTab(cible.slotRef)
  } catch (erreur) {
    /*
     * DÉGRADÉ, MAIS JAMAIS MUET.
     *
     * L'accès à l'application ne dépend pas du trousseau — on n'échoue donc
     * pas. Mais un `catch` vide a déjà coûté un déploiement : l'adoption ne se
     * faisait pas, rien ne le disait, et le seul symptôme était une
     * surveillance qui ne voyait jamais rien. Le motif part au journal du
     * navigateur, où le banc de parcours le lit.
     */
    // sast:derogation(SAST-005) navigateur : meme canal unique que ci-dessus. Le
    // motif vient d'une exception LOCALE (reseau, trousseau), jamais d'une
    // entree utilisateur, et se taire a deja coute un deploiement entier.
    console.warn(
      '[backoffice] adoption du compte impossible —',
      erreur instanceof Error ? erreur.message : 'erreur inconnue',
    )
  }
}
